/**
 * Pure helpers for the Company Onboarding "Additional onboarding files" multi-select
 * upload (backlog #2, UAT Section 5: "just check if the upload functionality is
 * properly implemented ... the advisor might upload multiple files at once").
 *
 * Extracted out of ProjectWorkspace.jsx's `handleOnboardingExtraUpload` so the
 * file-list normalization + failure-aggregation logic is unit-testable without
 * mounting the full workspace page.
 *
 * The backend endpoint (`companies_id_onboarding_assetsPost`,
 * policylens-backend/src/routes/routes.ts: `docUpload.single('file')`) is
 * intentionally single-file-per-request (it mints one assetId/blob/audit row per
 * call), so the frontend satisfies "upload multiple files at once" by letting the
 * advisor multi-select in one file-picker interaction and then posting each file
 * to that same endpoint in turn — no backend contract change required.
 */

/**
 * Normalize the `handleOnboardingExtraUpload` argument (a single File, or an
 * array/FileList of File objects) into a plain array with falsy entries dropped.
 * @param {*} fileOrFiles
 * @returns {File[]}
 */
export function normalizeUploadFileList(fileOrFiles) {
  const files = Array.isArray(fileOrFiles)
    ? fileOrFiles
    : fileOrFiles && typeof fileOrFiles.length === "number" && typeof fileOrFiles !== "string"
      ? Array.from(fileOrFiles)
      : [fileOrFiles];
  return files.filter(Boolean);
}

/**
 * Build the user-facing error message for a batch upload, given the total number
 * of files attempted and the list of failures (`{ name, reason }`).
 * @param {number} totalCount
 * @param {Array<{name: string, reason: string}>} failed
 * @returns {string|null} null when there were no failures.
 */
export function formatOnboardingUploadFailureMessage(totalCount, failed) {
  if (!failed || !failed.length) return null;
  if (failed.length === totalCount) return failed[0].reason || "Upload failed";
  return `${failed.length} of ${totalCount} files failed to upload (${failed.map((f) => f.name).join(", ")})`;
}
